/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react'
import PersonalisedTab from './PersonalisedTab'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { useDispatch, useSelector } from 'react-redux'
import { fetchFeedback } from '../../redux/feedbackSlice'
import { fetchCourseModule, selectCourseModule, selectCourseModuleContent, selectLessonBySlugs, selectModuleBySlug } from '../../redux/courseModuleSlice'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { fetchLessonQuiz, selectGeneratedQuizQuestion, selectQuizGeneratedForLesson } from '../../redux/lessonQuizSlice'
import { BarLoader, ClipLoader, SyncLoader } from 'react-spinners'
import { fetchUserActivityUpdate } from '../../redux/updateUserActivitySlice'
import { selectCourseList, selectCurrentCourseId } from '../../redux/courseListSlice'
import { Mixpanel } from '../../config/Mixpanel'
import constants, { removeWords } from '../../utilities/constants'
import { fetchKeyTakeaways } from '../../redux/keyTakeawaySlice'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { darcula } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { getAssessmentContent, getQuizQuestionText } from '../../utilities/videoPlayerApi'
import useModuleCurationPolling from '../../hooks/useModuleCurationPolling'
import { resolveCodeLanguage } from '../../utilities/codeLanguage'

const renderBoldMarkdown = (text) => text.split(/(\*\*[\s\S]+?\*\*)/g).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>
    }

    return part
})

const QuizSection = ({ 
    userGoal, 
    setUserGoal, 
    course_slug, 
    lesson_slug, 
    module_slug,
}) => {

    const courseModule = useSelector(selectCourseModule)
    const courseModuleContent = useSelector(selectCourseModuleContent)
    const moduleDetails = useSelector((state) => selectModuleBySlug(state, module_slug))
    const lessonDetails = useSelector((state) => selectLessonBySlugs(state, module_slug, lesson_slug))

    const existingUserGoal = courseModule?.user_goal
    const videoId = lessonDetails?.video_id || lessonDetails?.video_url?.split("?")?.[1]?.split("=")?.[1]
    const lessonId = lessonDetails?.lesson_id
    const content = lessonDetails?.resource?.content
    const contentType = lessonDetails?.resource?.type
    const language = lessonDetails?.resource?.language

    const codeExists = contentType === 'code'
    const textExists = contentType === 'text'

    const [ moduleIndex, setModuleIndex ] = useState(0)
    const [ lessonIndex, setLessonIndex ] = useState(0)
    const [ feedback, setFeedback ] = useState(lessonDetails?.quiz_analysis)
    const [ answer, setAnswer ] = useState(lessonDetails?.quiz_answer)
    const [navigationArray, setNavigationArray] = useState([])
    const [ isQuizLoading, setIsQuizLoading ] = useState(false)
    const [ isAnalysisLoading, setIsAnalysisLoading ] = useState(false)
    
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { startPolling } = useModuleCurationPolling()

    const subject_name = useSelector(selectCourseList)?.courseName
    const defaultCodeLanguage = language || removeWords(subject_name || '').toLowerCase()
    const codeLanguage = resolveCodeLanguage({
        lessonName: lessonDetails?.lesson_name,
        moduleName: moduleDetails?.module_name,
        fallbackLanguage: defaultCodeLanguage,
    })
    const courseId = useSelector(selectCurrentCourseId)
    const generatedQuizQuestion = useSelector((state) => selectGeneratedQuizQuestion(state, courseId, module_slug, lessonId))
    const quizGeneratedForLesson = useSelector((state) => selectQuizGeneratedForLesson(state, courseId, module_slug, lessonId))
    const quizQuestion = getQuizQuestionText(lessonDetails?.quiz_question) || generatedQuizQuestion
    const assessmentContent = useMemo(() => getAssessmentContent(feedback), [feedback])

    const refreshCourse = () => dispatch(fetchCourseModule({ course_id: courseId })).unwrap()

    const reconcileAfterFailure = async (error) => {
        console.error(error)
        try {
            await refreshCourse()
        } catch (refreshError) {
            console.error(refreshError)
        }
    }
    
    useEffect(() => {
        let navigateArray = []
        for (let i = 0; i < courseModuleContent?.length; i++){
            for(let j = 0; j < courseModuleContent?.[i]?.module_content?.length; j++){
                navigateArray.push(`/${course_slug}/${courseModuleContent?.[i]?.module_slug}/${courseModuleContent?.[i]?.module_content?.[j]?.lesson_slug}`)
            }
        }
        setNavigationArray(navigateArray)
    }, [])

    useEffect(() => {
        if (existingUserGoal?.length > 0 && !quizQuestion?.length && !quizGeneratedForLesson){
            setIsQuizLoading(true)
            const fetchData = async () => {
                setIsQuizLoading(true)
                try {
                    await dispatch(fetchLessonQuiz({ 
                        course_id: courseId,
                        module_slug,
                        video_id: videoId, 
                        lesson_id: lessonId, 
                        goal: existingUserGoal, 
                    })).unwrap()
                    await refreshCourse()
                } catch (err) {
                    await reconcileAfterFailure(err)
                } finally {
                    setIsQuizLoading(false)
                }
            }
           fetchData() 
        }
    }, [lesson_slug, quizQuestion?.length, quizGeneratedForLesson])

    useEffect(() => {
        setModuleIndex(courseModuleContent?.findIndex((item) => item?.module_slug === module_slug))
    }, [module_slug])

    useEffect(() => {
        setLessonIndex(courseModuleContent?.[moduleIndex]?.module_content?.findIndex((item) => item?.lesson_slug === lesson_slug))
    }, [moduleIndex, lesson_slug])

    useEffect(() => {
        setAnswer(lessonDetails?.quiz_answer)
        setFeedback(lessonDetails?.quiz_analysis)
    }, [lessonDetails])

    const handleSubmit = (e) => {
        e.preventDefault()
        Mixpanel.track(constants.Mixpanel.LESSON_PAGE_USER_GOAL_FROM_QUIZ_WAS_CLICKED)
        if (userGoal.length <= 0) {
            return toast.error("Enter your goal")
        }
        const fetchData = async () => {
            setIsQuizLoading(true)
            try {
            
                await dispatch(fetchLessonQuiz({ 
                    course_id: courseId,
                    module_slug,
                    video_id: videoId, 
                    lesson_id: lessonId, 
                    goal: userGoal, 
                })).unwrap()

                await dispatch(fetchKeyTakeaways({ 
                    course_id: courseId,
                    module_slug,
                    video_id: videoId, 
                    goal: userGoal, 
                    lesson_id: lessonId, 
                })).unwrap()

                await refreshCourse()
            } catch (error) {
                await reconcileAfterFailure(error)
            } finally {
                setIsQuizLoading(false)
            }
          };
        fetchData();
        setUserGoal('')
    }

    const handleQuizSubmit = (e) => {
        e.preventDefault()
        Mixpanel.track(constants.Mixpanel.LESSON_PAGE_QUIZ_WAS_SUBMITTED)
        if(!answer?.length){
            return toast.error("Enter your answer")
        }
        const fetchData = async () => {
            setIsAnalysisLoading(true)
            try {
                await dispatch(fetchFeedback({
                    course_id: courseId,
                    module_slug,
                    video_id: videoId, 
                    lesson_id: lessonId, 
                    question: quizQuestion,
                    answer: answer
                })).unwrap()

                const activityUpdate = await dispatch(fetchUserActivityUpdate({
                    course_id: courseId,
                    module_slug,
                    lesson_id: lessonDetails?.lesson_id, 
                    status: "completed" 
                })).unwrap()

                const updatedModuleIndex = activityUpdate?.course?.course_data?.findIndex(
                    (module) => module?.module_slug === module_slug
                )
                const nextModule = activityUpdate?.course?.course_data?.[updatedModuleIndex + 1]
                if (
                    activityUpdate?.module_progress === 100
                    && activityUpdate?.curation_status === 'pending'
                    && nextModule?.module_slug
                ) {
                    startPolling({
                        courseId,
                        moduleSlug: nextModule.module_slug,
                        onCompleted: (status) => {
                            if (!status?.is_locked) {
                                toast.success('Your next module is ready!', {
                                    toastId: `module-curation-completed-${updatedModuleIndex + 1}`,
                                })
                            }
                        },
                    })
                } else if (
                    activityUpdate?.module_progress === 100
                    && activityUpdate?.curation_status === 'completed'
                    && nextModule
                    && !nextModule?.is_locked
                ) {
                    toast.success('Your next module is ready!', {
                        toastId: `module-curation-completed-${updatedModuleIndex + 1}`,
                    })
                }
            }catch(error) {
                await reconcileAfterFailure(error)
            } finally {
                setIsAnalysisLoading(false)
            }
        }
        fetchData()
    }

    const handlePreviousLesson = () => { 
        Mixpanel.track(constants.Mixpanel.LESSON_PAGE_PREVIOUS_LESSON_BUTTON_CLICKED)
        let index = navigationArray?.findIndex((item) => item === `/${course_slug}/${module_slug}/${lesson_slug}`)
        navigate(navigationArray[index - 1])
    }

    const handleNextLesson = () => {
        Mixpanel.track(constants.Mixpanel.LESSON_PAGE_NEXT_LESSON_BUTTON_CLICKED)
        let index = navigationArray?.findIndex((item) => item === `/${course_slug}/${module_slug}/${lesson_slug}`)
        navigate(navigationArray[index + 1])
        
    }

    const parseQuestionWithCodeBlocks = (text) => {
        if (!text) return null;
        
        const parts = text.split('```');
        
        return parts.map((part, index) => {
            if (index % 2 === 0) {
                return part ? <span key={index}>{part}</span> : null;
            } else {
                const lines = part.split('\n');
                const firstLine = lines[0].trim();
                const hasLanguage = firstLine && !firstLine.includes(' ');
                
                let codeContent;
                if (hasLanguage) {
                    // Remove language line and trim empty lines
                    const codeLines = lines.slice(1);
                    // Remove leading empty lines
                    while (codeLines.length > 0 && codeLines[0].trim() === '') {
                        codeLines.shift();
                    }
                    // Remove trailing empty lines  
                    while (codeLines.length > 0 && codeLines[codeLines.length - 1].trim() === '') {
                        codeLines.pop();
                    }
                    codeContent = codeLines.join('\n');
                } else {
                    codeContent = part.trim();
                }
                
                return (
                    <SyntaxHighlighter 
                        language={removeWords(subject_name).toLowerCase()} 
                        style={darcula} 
                        wrapLongLines={true}
                        customStyle={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word', 
                            overflowX: 'hidden',
                            padding: '12px',
                            borderRadius: '12px',
                            marginTop: '12px',
                        }}
                        codeTagProps={{
                            style: {
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                            },
                        }}
                    >
                        {codeContent}
                    </SyntaxHighlighter>
                );
            }
        }).filter(Boolean);
    };

    return (
        <>
            {!existingUserGoal?.length > 0 ? (
                !isQuizLoading ? <div className='p-4 reactour-desktop-usergoal'>
                    <div className=''>
                        <h2 className='text-2xl font-semibold'>Quiz Question</h2>
                        <p className='italic w-[95%] opacity-50 text-sm mt-2 leading-[17px]'>Provide us your goal and we’ll personalise quiz for you</p>
                    </div>
                    <div className='mt-2 rounded-xl border border-white/10 bg-gradient-to-br from-[#17395A] to-[#102D47] px-4 py-5'>
                        <p>With this course, I want to _______</p>
                        <form onSubmit={handleSubmit}>
                            <input 
                                type="text"
                                placeholder='Get a job as a data scientist, get a raise of...'
                                value={userGoal}
                                onChange={(e) => setUserGoal(e.target.value)}
                                className='w-full bg-[#17395A] rounded-xl text-sm goal border-none placeholder:text-sm mt-2'
                            />
                            <div className='flex justify-end mt-4 mb-1'>
                                <button type='submit' disabled={!userGoal.length > 0} className='py-2 disabled:opacity-50 cursor-pointer px-5 rounded-xl bg-[#7FC241] text-[#071D2D]'>Submit</button>
                            </div>
                        </form>
                    </div>
                </div> : <div className='mx-4 mb-2 mt-4 flex h-[200px] flex-col items-center justify-center rounded-xl bg-[#17395A] lg:my-6'>
                            <p className='mb-2'>Loading</p>
                            <BarLoader color='#7FC241'/>
                        </div>) : 
            (!isQuizLoading ? <div className='p-4 reactour-quizsection'>
                <div className='relative'>
                    <h2 className='text-2xl font-semibold'>Quiz</h2>
                    <p className='italic w-[95%] opacity-50 text-sm mt-1 leading-[17px]'>Complete quiz to mark this lesson as complete</p>
                    <PersonalisedTab/>
                </div>
                <div className='mt-4 leading-[19px]'>
                    {parseQuestionWithCodeBlocks(quizQuestion)}
                    {codeExists ? <SyntaxHighlighter 
                        language={codeLanguage}
                        style={darcula} 
                        wrapLongLines={true}
                        customStyle={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word', 
                            overflowX: 'hidden',
                            padding: '12px',
                            borderRadius: '12px',
                            marginTop: '12px',
                            marginBottom: 0,
                        }}
                        codeTagProps={{
                            style: {
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                            },
                        }}
                    >
                        {content}
                    </SyntaxHighlighter> : null}
                    {textExists ? <p className='mt-4 rounded-xl bg-[#17395A]/40 p-4 leading-[140%] text-white/80'>{content}</p> : null}
                    <form onSubmit={handleQuizSubmit}>
                        <textarea
                            id='answer'
                            value={answer}
                            onChange={(e) => setAnswer(e.target.value)}
                            placeholder='Answer this question in 2-3 lines'
                            className='answer_text mt-4 h-28 w-full rounded-xl bg-[#17395A]'
                        />
                        {!isAnalysisLoading ? <button type='submit' disabled={!answer} className='py-3.5 disabled:opacity-50 w-2/5 mt-2 rounded-xl bg-[#7FC241] text-[#071D2D] font-semibold cursor-pointer'>{assessmentContent ? 'Resubmit' : 'Submit'}</button> :
                        <button type='submit' disabled={isAnalysisLoading} className='flex items-center justify-center py-3.5 disabled:opacity-50 w-2/5 mt-2 font-semibold rounded-xl bg-[#7FC241] text-[#071D2D]'>Analysing <ClipLoader color='#071D2D' size={15} className='ml-2'/></button>}
                    </form>
                </div>
                {assessmentContent ? <div className='mt-4'>
                    <h2 className='text-xl font-semibold'>Your Feedback</h2>
                    <div className='mt-2 rounded-xl bg-[#244968] p-4 leading-[19px]'>
                        {assessmentContent.type === 'text' ?
                            <p className='whitespace-pre-wrap break-words'>{renderBoldMarkdown(assessmentContent.feedback)}</p>
                            : <>
                                {assessmentContent.feedback ? <p>{assessmentContent.feedback}</p> : null}

                                {assessmentContent.strengths.length > 0 ? <div className={assessmentContent.feedback ? 'mt-3' : ''}>
                                    <h3 className='font-semibold'>Strengths:</h3>
                                    <ul className='mt-1 list-disc space-y-1 pl-5'>
                                        {assessmentContent.strengths.map((strength, index) => <li key={index}>{strength}</li>)}
                                    </ul>
                                </div> : null}

                                {assessmentContent.improvements.length > 0 ? <div className={assessmentContent.feedback || assessmentContent.strengths.length > 0 ? 'mt-3' : ''}>
                                    <h3 className='font-semibold'>To Improve:</h3>
                                    <ul className='mt-1 list-disc space-y-1 pl-5'>
                                        {assessmentContent.improvements.map((improvement, index) => <li key={index}>{improvement}</li>)}
                                    </ul>
                                </div> : null}
                            </>}
                    </div>
                </div> : null}
            </div> : <div className='mx-4 mb-2 mt-4 flex h-[200px] flex-col items-center justify-center rounded-xl bg-[#17395A] lg:my-6'>
                            <p className='mb-2'>Loading</p>
                        <SyncLoader color='#7FC241'/>
                    </div>)}
            <div className={`px-4 mt-1 flex ${!(moduleIndex === 0 && lessonIndex === 0) ? 'justify-between' : 'justify-end'}`}>
                {!(moduleIndex === 0 && lessonIndex === 0) && <div className='flex items-center cursor-pointer' onClick={handlePreviousLesson}>
                    <FontAwesomeIcon icon={faArrowLeft}/>
                    <p className='ml-1 font-semibold'>Previous Lesson</p>
                </div>}
                {!(moduleIndex === courseModuleContent?.length - 1 && lessonIndex === courseModuleContent?.[moduleIndex]?.module_content?.length - 1) && <div className={`flex items-center cursor-pointer ${assessmentContent ? 'bg-[#7FC241] text-[#071D2D] pl-2.5 pr-3 py-2 rounded-xl' : ''}`} onClick={handleNextLesson}>
                    <p className={`${assessmentContent ? 'mr-2' : 'mr-1'} font-semibold`}>{assessmentContent ? 'Next' : 'Next Lesson'}</p>
                    <FontAwesomeIcon icon={faArrowRight} className={assessmentContent ? 'arrow-move' : ''}/>
                </div>}
            </div>
        </>
    )
}

export default QuizSection
